import React, { useMemo } from 'react'
import { Card, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { useStore } from 'effector-react'
import { Wallet } from '../../../../views/widgets/walletCard/types'
import { path } from '../../../../libs'
import { $wallets, getWallets } from '../list/model'
import { useViewportMode } from '../useIsCompact'
import { CryptoIcon } from './CryptoIcon'
import styles from './styles.module.css'

export const WalletsTable: React.FC = () => {
  const wallets = useStore($wallets)
  const loading = useStore(getWallets.pending)
  const { isCompact, isWide } = useViewportMode()
  const walletWidth = isCompact ? 200 : isWide ? 360 : 260
  const localBalanceWidth = isCompact ? 240 : isWide ? 480 : 400
  const scrollX = isCompact ? 1060 : isWide ? 1480 : 1280

  const columns: ColumnsType<Wallet> = useMemo(
    () => [
      {
        title: <span className={styles.header}>Wallet</span>,
        dataIndex: 'name',
        key: 'name',
        width: walletWidth,
        sorter: (a, b) =>
          (a.paymentSystem?.name ?? a.name).localeCompare(
            b.paymentSystem?.name ?? b.name,
          ),
        render: (_, record) => (
          <Link
            to={path.finSystem.wallets.details(record.id)}
            target="_blank"
            className={styles.walletLink}
          >
            <CryptoIcon name={record.name} />
            {record.paymentSystem?.name ?? record.name}
          </Link>
        ),
      },
      {
        title: <span className={styles.header}>Total sum</span>,
        dataIndex: 'overallBalanceUsd',
        key: 'overallBalanceUsd',
        width: 200,
        sorter: (a, b) =>
          (a.overallBalanceUsd ?? 0) - (b.overallBalanceUsd ?? 0),
        render: (value: number | null) => {
          const formatted = Math.floor(value ?? 0).toLocaleString('ru')
          return (
            <span className={styles.title}>
              {formatted}{' '}
              <span className={`${styles.title} ${styles.muted}`}>USD</span>
            </span>
          )
        },
      },
      {
        title: <span className={styles.header}>Currencies</span>,
        key: 'currencies',
        width: 140,
        render: (_, record) => (
          <div>
            {record.localBalances.map((lb) => (
              <div key={lb.currency} className={styles.body}>
                {lb.currency}
              </div>
            ))}
          </div>
        ),
      },
      {
        title: <span className={styles.header}>Local balance</span>,
        key: 'localBalance',
        width: localBalanceWidth,
        render: (_, record) => (
          <div>
            {record.localBalances.map((lb) => (
              <div key={lb.currency} className={styles.body}>
                {Number(lb.sum).toLocaleString('ru')}{' '}
                <span className={`${styles.body} ${styles.muted}`}>
                  {lb.currency}
                </span>
              </div>
            ))}
          </div>
        ),
      },
      {
        title: <span className={styles.headerDanger}>Loss (30 last days)</span>,
        key: 'lossPercentage',
        dataIndex: 'lossPercentage',
        width: 200,
        render: (value: number) =>
          value ? (
            <span className={styles.body}>
              -{value}{' '}
              <span className={`${styles.body} ${styles.muted}`}>%</span>
            </span>
          ) : (
            <span className={styles.body}>—</span>
          ),
      },
      {
        title: <span className={styles.header}>Revision</span>,
        key: 'reconciledAt',
        dataIndex: 'reconciledAt',
        render: (value: string) => (
          <span className={styles.body}>
            {value ? dayjs(value).format('DD.MM.YYYY') : '—'}
          </span>
        ),
      },
    ],
    [walletWidth, localBalanceWidth],
  )

  return (
    <Card
      title={<span className={styles.cardTitle}>All wallets</span>}
      className={styles.card}
      classNames={{ header: styles.cardHeader, body: styles.cardBody }}
    >
      <div className={styles.tableWrapper}>
        <Table<Wallet>
          columns={columns}
          dataSource={wallets}
          rowKey="id"
          scroll={{ x: scrollX }}
          loading={loading}
          pagination={false}
          rowClassName={(_record, index) =>
            index % 2 === 1 ? 'ant-table-row-striped' : ''
          }
        />
      </div>
    </Card>
  )
}
